import { useEffect } from "react";
import { siteMeta } from "./seoConfig";

export default function useSEO({
  title,
  description,
  keywords = "",
  url = "",
  image = siteMeta.defaultImage,
  noindex = false,
}) {
  useEffect(() => {
    document.title = title
      ? `${title} | ${siteMeta.siteName}`
      : siteMeta.siteName;

    const setMeta = (name, content, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;

      let element = document.querySelector(selector);

      if (!element) {
        element = document.createElement("meta");
        if (property) {
          element.setAttribute("property", name);
        } else {
          element.setAttribute("name", name);
        }
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    const safeUrl =
      !url || url === "/" ? "/" : url.startsWith("/") ? url : `/${url}`;

    const baseUrl = siteMeta.siteUrl.replace(/\/$/, "");
    const canonicalUrl = new URL(safeUrl, baseUrl).toString();

    // Основные meta
    const cleanDescription = description?.trim() || "";

    setMeta("description", cleanDescription);
    const stopWords = new Set(["и", "в", "на", "с", "по", "для", "о", "из"]);

    const finalKeywords =
      keywords?.trim() ||
      description
        ?.toLowerCase()
        ?.replace(/[^\p{L}\p{N}\s]/gu, "")
        ?.split(/\s+/)
        ?.filter(Boolean)
        ?.filter((w) => !stopWords.has(w))
        ?.slice(0, 8)
        ?.join(", ") ||
      "";

    setMeta("keywords", finalKeywords);
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow");

    // Open Graph

    setMeta(
      "og:title",
      title ? `${title} | ${siteMeta.siteName}` : siteMeta.siteName,
      true,
    );
    setMeta("og:description", cleanDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:image", image, true);
    setMeta("og:site_name", siteMeta.siteName, true);
    setMeta("og:locale", "ru_RU", true);

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta(
      "twitter:title",
      title ? `${title} | ${siteMeta.siteName}` : siteMeta.siteName,
    );
    setMeta("twitter:description", cleanDescription);
    setMeta("twitter:image", image);

    // Гео
    setMeta("geo.region", "RU-SVE");
    setMeta("geo.placename", "Екатеринбург");
    setMeta("ICBM", "56.8389,60.6057");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Schema.org
    let schema = document.getElementById("schema-org");
    if (!schema) {
      schema = document.createElement("script");
      schema.type = "application/ld+json";
      schema.id = "schema-org";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalOrganization",
      name: siteMeta.organizationName,
      url: canonicalUrl,
      logo: siteMeta.defaultImage,
      telephone: siteMeta.phone,
      email: siteMeta.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: siteMeta.address,
        addressLocality: siteMeta.city,
        addressRegion: siteMeta.region,
        addressCountry: siteMeta.country,
      },
    });
  }, [title, description, keywords, url, image, noindex]);
}
